import { Badge, Card, Group, Stack, Text } from '@mantine/core';
import { SPREAD_MODE_LABELS, type SpreadSummary } from '@creativitas/shared';
import { AssetImage } from '@/components/ui/AssetImage';
import { LockedOverlay } from '@/components/domain/LockedOverlay';
import { pluralize } from '@/lib/plural';

type SpreadCardProps = {
  spread: SpreadSummary;
  lockedReason?: string | null;
  lockedHint?: string | null;
  onClick?: () => void;
};

/** Catalogue representation shared by all spread list states (§20). */
export function SpreadCard({ spread, lockedReason, lockedHint, onClick }: SpreadCardProps) {
  const body = (
    <Card
      withBorder
      padding="md"
      h="100%"
      onClick={lockedReason ? undefined : onClick}
      style={{
        borderColor: 'var(--border-subtle)',
        cursor: onClick && !lockedReason ? 'pointer' : 'default',
      }}
    >
      <Card.Section>
        <AssetImage
          src={spread.coverImage}
          alt={spread.title}
          ratio="7 / 5"
          fallbackLabel="Обложка не загружена"
        />
      </Card.Section>

      <Stack gap={7} mt="md">
        <Group gap={6} wrap="wrap">
          <Badge color={spread.layout === 'freeform' ? 'gold' : 'teal'}>
            {spread.layout === 'freeform' ? 'Свободный стол' : pluralize(spread.positionCount, 'позиция', 'позиции', 'позиций')}
          </Badge>
          <Badge variant="default">{SPREAD_MODE_LABELS[spread.mode]}</Badge>
        </Group>
        <Text fw={600} size="lg" lh={1.3}>
          {spread.title}
        </Text>
        {spread.description ? (
          <Text size="sm" c="var(--text-muted)" lineClamp={3}>
            {spread.description}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );

  return lockedReason ? (
    <LockedOverlay reason={lockedReason} hint={lockedHint ?? undefined} onOpen={onClick}>
      {body}
    </LockedOverlay>
  ) : body;
}
